import { log } from "../../shared/services/debug-logger";
import type {
  AdminDashboardLoadState,
  AdminDashboardSectionId,
  AdminDashboardSectionResult,
} from "../models/admin-dashboard.models";
import type { CronJobLogEnvironment } from "../models/admin-monitoring.models";
import { getAdminDashboardOverview } from "../services/admin-dashboard.service";
import { ADMIN_DASHBOARD_SECTIONS } from "../utils/admin-dashboard.constants";
import { useEffect, useState } from "react";

// Own this visit's summaries locally and discard late responses after refresh, permission changes, or navigation.
// The request key also hides the previous environment's figures before the next effect begins.
export const useAdminDashboard = (sectionIds: string, environment: CronJobLogEnvironment, viewerId: string) => {
  const [refreshVersion, setRefreshVersion] = useState(0);
  const requestKey = JSON.stringify([sectionIds, environment, viewerId, refreshVersion]);
  const [loadState, setLoadState] = useState<AdminDashboardLoadState>({ requestKey: "", results: {} });

  useEffect(() => {
    let isCurrentRequest = true;
    const allowedIds = sectionIds.split(",");
    const sections = ADMIN_DASHBOARD_SECTIONS.filter((section) => allowedIds.includes(section.id));
    log("STATE - useAdminDashboard - load", { count: sections.length, environment });
    let failed = 0;
    const saveResult = (id: AdminDashboardSectionId, result: AdminDashboardSectionResult) => {
      if (!isCurrentRequest) {
        return;
      }

      setLoadState((current) => ({
        requestKey,
        results: { ...(current.requestKey === requestKey ? current.results : {}), [id]: result },
      }));
    };
    const requests = sections.map((section) =>
      getAdminDashboardOverview(section, environment).then(
        (overview) => saveResult(section.id, { status: "ready", overview }),
        () => {
          failed += 1;
          saveResult(section.id, { status: "error" });
        },
      ),
    );

    void Promise.allSettled(requests).then(() => {
      if (isCurrentRequest) log("STATE - useAdminDashboard - settled", { count: sections.length, failed });
    });

    return () => {
      isCurrentRequest = false;
    };
  }, [sectionIds, environment, requestKey]);

  const results: AdminDashboardLoadState["results"] = loadState.requestKey === requestKey ? loadState.results : {};
  const isLoading = sectionIds.split(",").some((id) => id && !Object.prototype.hasOwnProperty.call(results, id));

  return {
    results,
    isLoading,
    refresh: () => {
      log("STATE - useAdminDashboard - refresh");
      setRefreshVersion((version) => version + 1);
    },
  };
};
